"use client";

import React, { useEffect, useState } from "react";
import Popup from "./popup";
import { promoBanners as promoBannersRaw, type PromoBanner } from "@/lib/promoBanners";

const COOKIE_PREFIX = "popup_";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, expires: Date) {
  if (typeof document === "undefined") return;
  const cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
  document.cookie = cookie;
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

export default function PopupGroup() {
  const [visible, setVisible] = useState(false);
  const [displayBanners, setDisplayBanners] = useState<PromoBanner[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const now = Math.floor(Date.now() / 1000);

    // Get active (non-expired) banners sorted by newest expiry first
    const active = promoBannersRaw.slice().sort((a, b) => b.expireAt - a.expireAt).filter((b) => b.expireAt > now);

    if (active.length === 0) {
      setDisplayBanners([]);
      setVisible(false);
      return;
    }

    // Filter out banners the user has already seen (cookie = "seen")
    const unseen = active.filter((b) => getCookie(COOKIE_PREFIX + b.id) !== "seen");

    setDisplayBanners(unseen);
    setVisible(unseen.length > 0);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const originalOverflow = document.body.style.overflow;

    if (visible) document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [visible]);

  function handlePopupClose(id: string) {
    const banner = displayBanners.find((b) => b.id === id);
    if (!banner) {
      setDisplayBanners((prev) => prev.filter((p) => p.id !== id));
      return;
    }
    const cookieName = COOKIE_PREFIX + id;
    const expiryDate = new Date(banner.expireAt * 1000);
    setCookie(cookieName, "seen", expiryDate);

    setDisplayBanners((prev) => {
      const next = prev.filter((p) => p.id !== id);
      if (next.length === 0) setVisible(false);
      return next;
    });
  }

  if (!visible) return null;

  return (
    <div
      className={`gtz-popup-overlay`}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.65)",
        zIndex: 9999,
        padding: 20,
      }}
      role="dialog"
      aria-modal="true"
    >
      {displayBanners.map((b) => (
        <Popup key={b.id} id={b.id} expireAt={b.expireAt} imageSrc={b.imageSrc} onClose={handlePopupClose} />
      ))}
    </div>
  );
}

